import { xAccessor, yAccessor } from './dot-accessors.js';
import { DotCanvas } from '../../../core/components/canvas/components/DotCanvas.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { DEFAULT_FILL_OPACITY } from '../../../core/constants.js';
import type { AxisIdToScales } from '../../context/XYChartScales.context.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  DotMetadata,
  DotSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { relAccessor } from '../../utils/value-representation.js';

interface DotDisplayProps {
  axisIdToScales: AxisIdToScales;
  series: DotSeriesWithDatapoints;
  metadata: DotMetadata;
}

export function DotDisplay({
  axisIdToScales,
  series,
  metadata,
}: DotDisplayProps) {
  const { height, width } = useGridPlotSize();
  const { axisIdToScaleDomain } = useXYChartData();

  const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
  const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
  const { xAxis, yAxis } = metadata;
  const xRelAcc = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAcc = relAccessor(yAxis, axisIdToScaleDomain);

  const { color, shape } = metadata;

  return (
    <DotCanvas
      key={series.id}
      data={series.data}
      canvasHeight={height}
      canvasWidth={width}
      color={() => color.Default}
      opacity={DEFAULT_FILL_OPACITY}
      x={xAccessor(xScale, xRelAcc)}
      y={yAccessor(yScale, yRelAcc)}
      shape={shape}
      enableDotOverflow
    />
  );
}

DotDisplay['displayName'] = 'DotDisplay';
